// İSG uzmanı portalı — kullanıcı arayüzü tercihleri. İki tercih: tablo satır yoğunluğu (S34-2
// design review §2 "Density toggle", `sm` 32px ↔ `md` 40px, `lg` sunulmaz) ve kenar çubuğu rail
// durumu (S34-3 §3 "Persistence and the reload jump"). İKİSİ de GLOBAL tek tercih (kullanıcı
// BAŞINA, sayfa/tablo başına DEĞİL) — "kompakt seviyorum"/"rail seviyorum" kullanıcıyı HER
// yerde takip eder. Anahtar `principal.uuid`e göre ayrılır — auth token'larının `idb`
// adaptörüyle KARIŞTIRILMAZ. `@isgportal/shared`'daki paylaşılan `useSiteContext` store'u
// (güvenlik düzeltmesi, S34-3 sonrası review) AYNI `uuid`-başına anahtar biçimini kullanır,
// ama BURADAKİ gibi bir `useState` DEĞİL — birden çok bileşenin PAYLAŞTIĞI bir zustand
// store'u, `hydrate`i bir `useEffect` içinde tetikler (bkz. `siteContext.ts` dosya başlığı);
// karıştırma — "aynı desen" yalnız anahtar biçimi için doğrudur, hook'un iç mimarisi için
// değil.
//
// `principal.uuid` `useAuthStore`'dan SENKRON gelir (JWT'den çözülmüş, ağ turu yok) — `useMe`
// (asenkron `GET /me`) DEĞİL; bu yüzden tercih ilk render'da hazırdır, sonradan sıçramaz. Rail
// durumu için bu KRİTİK (design review §3): `useMe`ye bağlı olsaydı kenar çubuğu her yeniden
// yüklemede önce expanded render edip sonra rail'e SIÇRARDI.

import { useAuthStore } from '@isgportal/shared';
import { useCallback, useState } from 'react';

export type TableDensity = 'sm' | 'md';

const DEFAULT_DENSITY: TableDensity = 'md';

function storageKey(uuid: string): string {
  return `isgportal.ui.${uuid}.tableDensity`;
}

function isTableDensity(value: string | null): value is TableDensity {
  return value === 'sm' || value === 'md';
}

export function readTableDensity(uuid: string): TableDensity {
  try {
    const raw = window.localStorage.getItem(storageKey(uuid));
    return isTableDensity(raw) ? raw : DEFAULT_DENSITY;
  } catch {
    return DEFAULT_DENSITY;
  }
}

export function writeTableDensity(uuid: string, density: TableDensity): void {
  try {
    window.localStorage.setItem(storageKey(uuid), density);
  } catch {
    // Depolama kullanılamıyor (gizli mod/kota) — sessizce yok say, geçiş bu oturumda çalışmaya devam eder.
  }
}

/**
 * Tablo yoğunluğu — kullanıcı başına kalıcı, `TableToolbar`/`Table`e doğrudan geçirilir.
 * `uuid` yoksa (oturum henüz hidrate olmadı / kullanıcı principal'ı değil) bellek-içi
 * varsayılana düşer, kalıcılaştırmaz.
 */
export function useTableDensity(): [TableDensity, (next: TableDensity) => void] {
  const uuid = useAuthStore((s) => s.principal?.uuid ?? null);
  const [density, setDensityState] = useState<TableDensity>(() =>
    uuid ? readTableDensity(uuid) : DEFAULT_DENSITY,
  );

  const setDensity = useCallback(
    (next: TableDensity) => {
      setDensityState(next);
      if (uuid) {
        writeTableDensity(uuid, next);
      }
    },
    [uuid],
  );

  return [density, setDensity];
}

// ---- Kenar çubuğu rail durumu (S34-3, design review §3 "Persistence and the reload jump") ----

function navRailStorageKey(uuid: string): string {
  return `isgportal.ui.${uuid}.navCollapsed`;
}

/**
 * Kenar çubuğu — kullanıcı başına kalıcı, `AppLayout`/`SideNav`e doğrudan geçirilir. `uuid`
 * yoksa (oturum henüz hidrate olmadı) bellek-içi varsayılana (`expanded`) düşer,
 * kalıcılaştırmaz — `useTableDensity`ile AYNI desen.
 */
export function useNavRailCollapsed(): [boolean, (next: boolean) => void] {
  const uuid = useAuthStore((s) => s.principal?.uuid ?? null);
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    if (!uuid) {
      return false;
    }
    try {
      return window.localStorage.getItem(navRailStorageKey(uuid)) === '1';
    } catch {
      return false;
    }
  });

  const setCollapsed = useCallback(
    (next: boolean) => {
      setCollapsedState(next);
      if (!uuid) {
        return;
      }
      try {
        window.localStorage.setItem(navRailStorageKey(uuid), next ? '1' : '0');
      } catch {
        // Depolama kullanılamıyor (gizli mod/kota) — sessizce yok say, geçiş bu oturumda çalışmaya devam eder.
      }
    },
    [uuid],
  );

  return [collapsed, setCollapsed];
}
